import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ListSubheader,
  MenuItem,
  Select as MuiSelect,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";

function optionText(node) {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(optionText).join(" ");
  if (!React.isValidElement(node)) return "";

  const props = node.props || {};
  return [
    props.children,
    props.primary,
    props.secondary,
    props.label,
    props.value,
  ]
    .map(optionText)
    .filter(Boolean)
    .join(" ");
}

function mergeMenuProps(menuProps) {
  return {
    ...menuProps,
    disableAutoFocusItem: true,
    PaperProps: {
      ...menuProps?.PaperProps,
      sx: {
        maxHeight: 420,
        ...menuProps?.PaperProps?.sx,
      },
    },
    MenuListProps: {
      ...menuProps?.MenuListProps,
      autoFocus: false,
    },
  };
}

export default function SearchableSelect({
  children,
  MenuProps,
  onClose,
  onOpen,
  open: openProp,
  searchable = true,
  searchPlaceholder,
  ...props
}) {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchInputRef = useRef(null);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  const childOptions = useMemo(() => React.Children.toArray(children), [children]);
  const normalizedSearch = search.trim().toLowerCase();
  const visibleChildren = useMemo(() => {
    if (!searchable || !normalizedSearch) return childOptions;
    return childOptions.filter((child) =>
      optionText(child).toLowerCase().includes(normalizedSearch)
    );
  }, [childOptions, normalizedSearch, searchable]);

  const handleOpen = (event) => {
    setSearch("");
    if (!isControlled) setInternalOpen(true);
    onOpen?.(event);
  };

  const handleClose = (event) => {
    setSearch("");
    if (!isControlled) setInternalOpen(false);
    onClose?.(event);
  };

  useEffect(() => {
    if (!open || !searchable) return undefined;
    const frame = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, search, searchable]);

  const searchBox = searchable ? (
    <ListSubheader
      disableSticky
      sx={{ bgcolor: "background.paper", px: 1.5, py: 1 }}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key !== "Escape") event.stopPropagation();
      }}
    >
      <TextField
        inputRef={searchInputRef}
        autoFocus
        fullWidth
        size="small"
        value={search}
        placeholder={searchPlaceholder || t("search", { defaultValue: "Search" })}
        onChange={(event) => setSearch(event.target.value)}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key !== "Escape") event.stopPropagation();
        }}
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, opacity: 0.65 }} />,
        }}
      />
    </ListSubheader>
  ) : null;

  return (
    <MuiSelect
      {...props}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      MenuProps={mergeMenuProps(MenuProps)}
    >
      {searchBox}
      {visibleChildren.length > 0 ? (
        visibleChildren
      ) : (
        <MenuItem disabled value="__searchable_select_no_results__">
          {t("common.noResults", { defaultValue: "No results" })}
        </MenuItem>
      )}
    </MuiSelect>
  );
}
